<template>
  <div class="ocr-report-page-preview">
    <div class="ocr-report-page-preview-head">
      <span>{{ headLabel }}</span>
      <div class="ocr-report-page-preview-actions">
        <v-btn icon size="x-small" variant="text" :disabled="pageIndex <= 0" @click="changePage(-1)">
          <v-icon size="18">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon size="x-small" variant="text" :disabled="pageIndex >= maxPage" @click="changePage(1)">
          <v-icon size="18">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
    <div v-if="loading" class="ocr-report-page-preview-placeholder">Loading page…</div>
    <p v-else-if="error" class="ocr-report-page-preview-error">{{ error }}</p>
    <img v-else-if="objectUrl" :src="objectUrl" class="ocr-report-page-preview-img" alt="" />
    <p v-else class="ocr-report-page-preview-placeholder">Select a table to preview its page.</p>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrReportPagePreview",
  props: {
    filename: { type: String, default: null },
    pageIndex: { type: Number, default: 0 },
    maxPage: { type: Number, default: 0 },
    tableIndex: { type: Number, default: null },
    highlightTables: { type: Boolean, default: true },
    activeTableIndex: { type: Number, default: null },
  },
  emits: ["update:pageIndex", "update:page-index"],
  data() {
    return { loading: false, error: "", objectUrl: "", reloadToken: 0, reloadQueued: false };
  },
  computed: {
    displayPage() {
      return (this.pageIndex ?? 0) + 1;
    },
    headLabel() {
      if (this.tableIndex != null) return `Table image · page ${this.displayPage}`;
      return `Page ${this.displayPage}`;
    },
  },
  watch: {
    filename: {
      immediate: true,
      handler() { this.queueReload(); },
    },
    pageIndex() { this.queueReload(); },
    tableIndex() { this.queueReload(); },
    highlightTables() { this.queueReload(); },
    activeTableIndex() { this.queueReload(); },
  },
  beforeUnmount() {
    this.reloadToken += 1;
    this.revoke();
  },
  methods: {
    queueReload() {
      if (this.reloadQueued) return;
      this.reloadQueued = true;
      this.$nextTick(() => {
        this.reloadQueued = false;
        this.reload();
      });
    },
    async readError(error) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (typeof json.detail === "string" && json.detail) return json.detail;
          if (Array.isArray(json.detail)) {
            return json.detail.map((d) => d.msg || String(d)).join("; ");
          }
        } catch {
          /* response body was not JSON */
        }
      }
      return formatOcrApiError(error, "Page preview unavailable.");
    },
    revoke() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = "";
      }
    },
    changePage(delta) {
      const next = Math.min(Math.max(0, this.pageIndex + delta), this.maxPage);
      this.$emit("update:pageIndex", next);
    },
    async reload() {
      const requestId = this.reloadToken + 1;
      this.reloadToken = requestId;
      this.revoke();
      this.error = "";
      if (!this.filename) {
        this.loading = false;
        return;
      }
      this.loading = true;
      const path = this.tableIndex != null
        ? `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/preview`
        : `api/v1/reports/${encodeURIComponent(this.filename)}/pages/${this.pageIndex}/preview`;
      const params = this.tableIndex != null
        ? undefined
        : {
            highlight: this.highlightTables ? "true" : "false",
            ...(this.activeTableIndex != null ? { table_index: this.activeTableIndex } : {}),
          };
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (requestId !== this.reloadToken) return;
        try {
          const { data } = await ocrApi.get(path, { responseType: "blob", params });
          if (requestId !== this.reloadToken) return;
          this.objectUrl = URL.createObjectURL(data);
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
          if (attempt === 0 && requestId === this.reloadToken) {
            await new Promise((resolve) => setTimeout(resolve, 400));
          }
        }
      }
      if (requestId !== this.reloadToken) return;
      if (lastError) this.error = await this.readError(lastError);
      this.loading = false;
    },
  },
};
</script>
